import { ExampleSentence, SearchResult } from 'core/interfaces/dictionary-interfaces'
import { EngWordQueryResult, QueryResult, ThaiWordQueryResult } from 'models/query-result.model'
import { SentenceWithTranslit, WordTranslation, WordTranslationLine } from './interfaces'
import { isNumericRegex, isThaiText } from './generic-utils/strings'
import thai2englishApi from '../api/thai2english-api'
import { preprocessQuery } from './preprocessQuery'
import { splitQueryIntoLines } from './splitSentences'
import { getTranslationsFromMeaning } from 'lib/getTranslationsFromMeaning'
import { groupSeparableTokens } from 'lib/separable-matching'
import { narrowMeaningsToSenseGroup } from 'lib/sense-groups'

// Covers 95.9% of Thai headwords (99.96% of the 5,000 most common) and matches
// the segmenter's MAX_TOKEN_LENGTH bound for "plausibly a single token".
const MAX_PROBABLE_THAI_WORD_LENGTH = 20

const zipSentencesWithTranslations = (
   sentences: ExampleSentence[],
   translations: WordTranslation[]
): SentenceWithTranslit[] =>
   sentences.map((s, index) => ({
      ...s,
      phonetic: translations[index]?.phonetic || '',
      words: translations[index]?.wordObjects || [],
   }))

// One flat, deterministic list of a word's sentences — the word-level fallback
// (unclassified) first, then each meaning's classified sentences in meaning
// order — so a single translation lookup covers them all and the zipped
// results can be sliced back into their groups by index.
type FlatSentenceEntry = { meaningId: number | null; sentence: ExampleSentence }

const flattenExampleSentences = (
   wordLevelSentences: ExampleSentence[],
   meanings: { id: number; exampleSentences?: ExampleSentence[] }[]
): FlatSentenceEntry[] => [
   ...wordLevelSentences.map((s) => ({ meaningId: null, sentence: s })),
   ...meanings.flatMap((m) =>
      (m.exampleSentences ?? []).map((s) => ({ meaningId: m.id, sentence: s }))
   ),
]

const groupZippedSentences = (
   entries: FlatSentenceEntry[],
   translations: WordTranslation[]
): {
   exampleSentences: SentenceWithTranslit[]
   exampleSentencesByMeaningId: Record<number, SentenceWithTranslit[]>
} => {
   const zipped = zipSentencesWithTranslations(
      entries.map((e) => e.sentence),
      translations
   )
   const exampleSentences: SentenceWithTranslit[] = []
   const exampleSentencesByMeaningId: Record<number, SentenceWithTranslit[]> = {}
   entries.forEach((entry, index) => {
      if (entry.meaningId === null) {
         exampleSentences.push(zipped[index])
      } else {
         ;(exampleSentencesByMeaningId[entry.meaningId] ??= []).push(zipped[index])
      }
   })
   return { exampleSentences, exampleSentencesByMeaningId }
}

// Builds a Thai dictionary-page result from an exact match (Thai-script input
// or a unique transliteration hit). The search response embeds the sentence-
// token lookups, so the whole page renders from that single request; fall back
// to a dictionary call only when talking to a worker that predates them.
export const buildThaiWordResult = async (
   thaiDictionaryWord: NonNullable<SearchResult['thaiExactMatch']>,
   searchResults: SearchResult
): Promise<ThaiWordQueryResult> => {
   const sentenceEntries = flattenExampleSentences(
      thaiDictionaryWord.exampleSentences,
      thaiDictionaryWord.meanings
   )
   const thaiSentences = sentenceEntries.map((e) => e.sentence.thaiSentence)
   const spacedSentencesText = sentenceEntries.map((e) => e.sentence.thaiSentenceSpaced)
   // Each token's sense within its sentence, so the tooltip shows only that sense.
   const sentenceSenses = sentenceEntries.map((e) => e.sentence.senseByWord)

   let sentences: WordTranslation[] = []
   try {
      sentences = searchResults.thaiWordLookups
         ? thai2englishApi.buildTranslationsFromLookups(
              thaiSentences,
              spacedSentencesText,
              searchResults.thaiWordLookups,
              sentenceSenses
           )
         : await thai2englishApi.getTranslations(thaiSentences, spacedSentencesText, sentenceSenses)
   } catch (err) {
      console.warn('Failed to fetch translations for Thai dictionary word sentences:', err)
   }

   const grouped = groupZippedSentences(sentenceEntries, sentences)

   // On a separable verb's page (ลองดู), sentences containing the headword
   // split (ลอง … ดู) carry the parts' token indexes — group those tokens so
   // both parts highlight together and show the entry's meaning.
   if (thaiDictionaryWord.separable) {
      const canonicalWord = thaiDictionaryWord.word
      const allGroups = [
         grouped.exampleSentences,
         ...Object.values(grouped.exampleSentencesByMeaningId),
      ]
      for (const sentence of allGroups.flat()) {
         if (!sentence?.partIndexes?.length) continue
         // Narrow the grouped meaning to the sense this sentence uses (its
         // canonical link's meaning_id) widened to its linked-sense group,
         // matching the per-token narrowing done for ordinary words; keep all
         // senses when unclassified.
         const senseId = sentence.senseByWord?.[canonicalWord]
         const meanings = narrowMeaningsToSenseGroup(thaiDictionaryWord.meanings, senseId).map(
            (m) => ({
               meaning: m.meaning || m.explanation || '',
            })
         )
         groupSeparableTokens(
            sentence.words,
            { word: canonicalWord, t2e: thaiDictionaryWord.t2e, meanings },
            sentence.partIndexes
         )
      }
   }

   return {
      type: 'thai-word',
      dictionaryWord: thaiDictionaryWord,
      ...grouped,
      pageUrl: '',
   }
}

// Builds an English dictionary-page result from an exact headword match. Like
// the Thai builder, the search response embeds every Thai token lookup the page
// needs — the meanings' Thai words as well as the example sentences — so one
// request covers the whole page.
export const buildEngWordResult = async (
   engDictionaryWord: NonNullable<SearchResult['engExactMatch']>,
   searchResults: SearchResult
): Promise<EngWordQueryResult> => {
   const wordsForTranslation = engDictionaryWord.meanings.flatMap((m) =>
      getTranslationsFromMeaning(m)
   )

   const sentenceEntries = flattenExampleSentences(
      engDictionaryWord.exampleSentences,
      engDictionaryWord.meanings
   )
   const thaiSentences = sentenceEntries.map((e) => e.sentence.thaiSentence)
   const spacedThaiSentences = sentenceEntries.map((e) => e.sentence.thaiSentenceSpaced)
   // The meaning-word portion has no sentence context; only the trailing
   // example sentences carry per-token senses.
   const combinedSenses = [
      ...wordsForTranslation.map(() => undefined),
      ...sentenceEntries.map((e) => e.sentence.senseByWord),
   ]

   // One combined lookup for the meanings' Thai words and the example
   // sentences. The search response embeds the lookups for both, so the
   // whole page renders from that single request; fall back to a
   // dictionary call only when talking to a worker that predates them.
   let engDictionaryTranslations: WordTranslation[] = []
   let sentences: WordTranslation[] = []
   try {
      const combined = searchResults.thaiWordLookups
         ? thai2englishApi.buildTranslationsFromLookups(
              [...wordsForTranslation, ...thaiSentences],
              [...wordsForTranslation, ...spacedThaiSentences],
              searchResults.thaiWordLookups,
              combinedSenses
           )
         : await thai2englishApi.getTranslations(
              [...wordsForTranslation, ...thaiSentences],
              [...wordsForTranslation, ...spacedThaiSentences],
              combinedSenses
           )
      engDictionaryTranslations = combined.slice(0, wordsForTranslation.length)
      sentences = combined.slice(wordsForTranslation.length)
   } catch (err) {
      console.warn('Failed to fetch translations for English word:', err)
   }

   return {
      type: 'eng-word',
      engDictionaryWord,
      engDictionaryTranslations,
      ...groupZippedSentences(sentenceEntries, sentences),
   }
}

const translateQuery = async (query: string): Promise<QueryResult> => {
   // A sentence per line, not just a line per line: the worker translates,
   // slices and caches by line, so several sentences sharing one line lose
   // everything after the first. See splitSentences.ts.
   const queries = splitQueryIntoLines(query)

   const workerResults = await thai2englishApi.translateWithWorker(queries)
   const thaiTranslationLines: WordTranslationLine[] = workerResults.map((result) => ({
      ...result.wordTranslation,
      englishTranslation: result.englishTranslation,
      translationFailed: result.translationFailed,
      translationUnavailable: result.translationUnavailable,
   }))

   return {
      type: 'translation',
      thaiTranslationLines,
      model: workerResults[0]?.model,
      translationUnavailable: workerResults.some((result) => result.translationUnavailable),
   }
}

const processQuery = async (rawQuery: string): Promise<QueryResult> => {
   const query = preprocessQuery(rawQuery)

   // If there's no text at all, there's nothing we can do
   if (!query) {
      return { type: 'empty-query' }
   }

   // A multi-line query can only be a translation job — no dictionary entry
   // contains a newline — so skip the search round trips entirely.
   if (query.includes('\n')) {
      return translateQuery(query)
   }

   const searchResults = await thai2englishApi.searchDictionary(query)

   // English text
   if (!isThaiText(query) && !isNumericRegex.test(query)) {
      // English dictionary word
      if (searchResults.engExactMatch) {
         return buildEngWordResult(searchResults.engExactMatch, searchResults)
      }

      // might be multiple english words, or translit
      const { engMatches, translitMatches } = searchResults
      const isPhrase = query.includes(' ')
      // English prefix matches are per-word "did you mean" suggestions and are
      // meaningless for a multi-word phrase; transliteration matches ignore
      // spacing (the key collapses it) so they stay useful for phrases like
      // "mai dai".
      const usableEngMatches = isPhrase ? [] : engMatches
      const hasSearchMatches = usableEngMatches.length > 0 || translitMatches.length > 0

      // A romanization that resolved to a single Thai word arrives as
      // thaiExactMatch and is routed to that word's dictionary page just below;
      // only show the "did you mean" list when there's no single-word page.
      if (hasSearchMatches && !searchResults.thaiExactMatch) {
         // Get the meanings of the Thai words found. The words double as their
         // own spaced queries so that meanings containing spaces stay on the
         // dictionary lookup instead of being routed to the translation worker.
         const thaiWordsFromEngSearch = usableEngMatches.flatMap((word) =>
            word.meanings.flatMap((meaning) => meaning.meaning.split(';').map((m) => m.trim()))
         )

         let translations: WordTranslation[] = []
         try {
            translations = await thai2englishApi.getTranslations(
               thaiWordsFromEngSearch,
               thaiWordsFromEngSearch
            )
         } catch (err) {
            console.warn('Failed to fetch translations for English search matches:', err)
         }

         return {
            type: 'search',
            thaiMatches: [],
            engMatches: usableEngMatches,
            translitMatches,
            translations,
         }
      }

      // A single word with "no matches" means nothing when the search itself
      // failed — surface an error (with retry) rather than sending what is
      // probably a dictionary word to the translation worker.
      if (!isPhrase && searchResults.searchFailed) {
         throw new Error('Dictionary search unavailable')
      }
      // Otherwise fall through to the translation worker
   }

   // Thai is written without spaces, so a short no-space query is probably a
   // single dictionary word; don't cache an LLM translation of it just because
   // the search was down. Longer unspaced text is probably a sentence — let it
   // translate even during an outage.
   if (
      searchResults.searchFailed &&
      isThaiText(query) &&
      !query.includes(' ') &&
      query.length <= MAX_PROBABLE_THAI_WORD_LENGTH
   ) {
      throw new Error('Dictionary search unavailable')
   }

   // Thai text or Thai dictionary id
   if (searchResults.thaiExactMatch) {
      return buildThaiWordResult(searchResults.thaiExactMatch, searchResults)
   }

   // If it's not a single Thai word, it's multiple
   return translateQuery(query)
}

export default processQuery
